import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, Download, FileText, Building2, MapPin, 
  User, Calendar, Recycle, Truck, CheckCircle, CreditCard
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// California Flag component
const CaliforniaFlag = ({ className = "w-8 h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_green-builder-3/artifacts/dnderd21_Cali%20FLAG.png" 
    alt="California" 
    className={`${className} object-contain`}
  />
);

const ProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/projects/${projectId}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      } else {
        toast.error('Project not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = project.project_type === 'final_report' 
        ? `${API}/generate/final-report/${projectId}`
        : `${API}/generate/cwmp/${projectId}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.project_type === 'cwmp' ? 'CWMP' : 'FinalReport'}_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloading(false);
    }
  };

  const handlePayment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          tier: 'standard',
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const checkout = await response.json();
      window.location.href = checkout.url;
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) return null;

  const isPaid = project.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-primary/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
              <CaliforniaFlag className="w-10 h-10" />
              <span className="font-bold text-xl text-foreground font-['Manrope']">Project Details</span>
            </div>
            
            <div className="flex items-center gap-3">
              {isPaid ? (
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="download-btn"
                >
                  {downloading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handlePayment}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="pay-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Complete Payment
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground font-['Manrope']" data-testid="project-title">
                {project.project_name}
              </h1>
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {project.address}, {project.city}, {project.county}, CA {project.zip_code}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge className={isPaid ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                {isPaid ? 'Paid' : 'Payment Pending'}
              </Badge>
              <Badge variant="outline">
                {project.project_type === 'cwmp' ? 'CWMP' : 'Final Report'}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Details */}
            <Card className="border-primary/20" data-testid="project-details-card">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Building Type</p>
                    <p className="text-foreground font-medium capitalize">{project.building_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Scope</p>
                    <p className="text-foreground font-medium capitalize">{project.scope}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Square Footage</p>
                    <p className="text-foreground font-medium">{project.square_footage?.toLocaleString()} sq ft</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Construction Type</p>
                    <p className="text-foreground font-medium capitalize">{project.construction_type}</p>
                  </div>
                  {project.valuation && (
                    <div>
                      <p className="text-muted-foreground">Valuation</p>
                      <p className="text-foreground font-medium">${project.valuation?.toLocaleString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Stories</p>
                    <p className="text-foreground font-medium">{project.stories}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Waste Estimate */}
            <Card className="border-primary/20" data-testid="waste-estimate-card">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Recycle className="w-5 h-5 text-primary" />
                  Waste Estimate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 rounded-lg bg-primary/10">
                    <p className="text-2xl font-bold text-primary">{project.estimated_waste?.total_tons?.toFixed(1)}</p>
                    <p className="text-sm text-muted-foreground">Total Tons</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-primary/10">
                    <p className="text-2xl font-bold text-primary">{project.estimated_waste?.total_lbs?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Pounds</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-green-500/10">
                    <p className="text-2xl font-bold text-green-400">{project.target_diversion}%</p>
                    <p className="text-sm text-muted-foreground">Target Diversion</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-2 text-foreground">Material</th>
                        <th className="text-right py-2 text-foreground">Est. Tons</th>
                        <th className="text-right py-2 text-foreground">% of Total</th>
                        <th className="text-left py-2 text-foreground">Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.materials?.map((material, index) => (
                        <tr key={index} className="border-b border-primary/10">
                          <td className="py-2 text-foreground capitalize">{material.material?.replace('_', ' ')}</td>
                          <td className="py-2 text-right text-muted-foreground">{material.estimated_tons?.toFixed(2)}</td>
                          <td className="py-2 text-right text-muted-foreground">{material.percentage?.toFixed(1)}%</td>
                          <td className="py-2 text-muted-foreground capitalize">{material.diversion_method}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Facilities */}
            {project.facilities?.length > 0 && (
              <Card className="border-primary/20" data-testid="facilities-card">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary" />
                    Selected Facilities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.facilities.map((facility, index) => (
                      <div key={index} className="p-3 rounded-lg border border-primary/20">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-foreground">{facility.name}</p>
                            <p className="text-sm text-muted-foreground">{facility.address}</p>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400">
                            {facility.diversion_rate}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Owner Info */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Owner
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground font-medium">{project.owner_name}</p>
                {project.owner_email && <p className="text-sm text-muted-foreground">{project.owner_email}</p>}
                {project.owner_phone && <p className="text-sm text-muted-foreground">{project.owner_phone}</p>}
              </CardContent>
            </Card>

            {/* Contractor Info */}
            {project.contractor_name && (
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Contractor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground font-medium">{project.contractor_name}</p>
                  {project.contractor_license && (
                    <p className="text-sm text-muted-foreground">License: {project.contractor_license}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Compliance */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Path</span>
                  <span className="text-foreground">{project.compliance_path === 'option1' ? '65% Diversion' : '50% + Prescriptive'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Target</span>
                  <span className="text-foreground">{project.target_diversion}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subcontractor Ack.</span>
                  <span className="text-foreground">{project.subcontractor_acknowledgment ? 'Yes' : 'No'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Jurisdiction Notes */}
            {project.jurisdiction_notes?.notes && (
              <Card className="border-yellow-500/30 bg-yellow-500/5">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-yellow-500 mb-1">Jurisdiction Notice</p>
                  <p className="text-sm text-muted-foreground">{project.jurisdiction_notes.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Dates */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-foreground">{new Date(project.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span className="text-foreground">{new Date(project.updated_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
